import { useState, useEffect } from "react";
import { Loader2, AlertCircle, Video } from "lucide-react";
import { getStreamVideoStatus } from "@/lib/api";

/**
 * Component for displaying Cloudflare Stream videos with processing status.
 * Polls for video status until ready, then displays the player.
 */
export default function CloudflareVideoPlayer({ 
  videoId, 
  className = "",
  onReady,
  onError 
}) {
  const [status, setStatus] = useState("loading");
  const [videoData, setVideoData] = useState(null);
  const [error, setError] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    if (!videoId) return;

    let pollInterval;
    let isMounted = true;

    const checkStatus = async () => {
      try {
        const response = await getStreamVideoStatus(videoId);
        if (!isMounted) return;

        const data = response.data;
        setVideoData(data);

        if (data.ready_to_stream) {
          setStatus("ready");
          if (pollInterval) clearInterval(pollInterval);
          if (onReady) onReady(data);
        } else if (data.status === "error" || data.error_reason) {
          setStatus("error");
          setError(data.error_reason || "Video processing failed");
          if (pollInterval) clearInterval(pollInterval);
          if (onError) onError(data.error_reason);
        } else {
          setStatus("processing");
        }
      } catch (err) {
        console.error("Error checking video status:", err);
        // Don't stop polling on temporary errors
        if (pollCount > 60) {
          setStatus("error");
          setError("Timeout waiting for video");
          if (pollInterval) clearInterval(pollInterval);
        }
      }
    };

    // Initial check
    checkStatus();

    // Poll every 3 seconds while processing
    pollInterval = setInterval(() => {
      setPollCount(c => c + 1);
      checkStatus();
    }, 3000);

    return () => {
      isMounted = false;
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [videoId, onReady, onError, pollCount]);

  // Loading state
  if (status === "loading") {
    return (
      <div className={`w-full h-64 rounded-lg bg-gradient-to-br from-gray-900 to-gray-800 flex flex-col items-center justify-center ${className}`}>
        <Loader2 className="w-10 h-10 text-white/60 animate-spin" />
        <p className="text-white/60 text-sm mt-3 font-['Nunito']">Loading video...</p>
      </div>
    );
  }

  // Error state
  if (status === "error") {
    return (
      <div className={`w-full h-64 rounded-lg bg-red-900/20 border border-red-500/30 flex flex-col items-center justify-center ${className}`}>
        <AlertCircle className="w-10 h-10 text-red-400" />
        <p className="text-red-300 text-sm mt-3 font-['Nunito'] font-medium">Video processing failed</p>
        <p className="text-red-400/70 text-xs mt-1 font-['Nunito']">{error}</p>
      </div>
    );
  }

  // Processing state
  if (status === "processing") {
    const pctComplete = videoData?.pct_complete || 0;
    return (
      <div 
        className={`w-full h-64 rounded-lg bg-gradient-to-br from-[#1a1a2e] to-[#16213e] flex flex-col items-center justify-center ${className}`}
        data-testid="cloudflare-video-processing"
      >
        <div className="relative">
          <Video className="w-12 h-12 text-blue-400/40" />
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
          </div>
        </div>
        
        <p className="text-white/80 text-sm mt-4 font-['Nunito'] font-medium">
          Processing video...
        </p>
        
        {/* Progress bar */}
        <div className="w-48 h-2 bg-white/10 rounded-full mt-3 overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all duration-500"
            style={{ width: `${pctComplete}%` }}
          />
        </div>
        
        <p className="text-white/50 text-xs mt-2 font-['Nunito']">
          {pctComplete}% complete
        </p>
        
        <p className="text-white/40 text-xs mt-1 font-['Nunito']">
          This may take a few minutes for longer videos
        </p>
      </div>
    );
  }

  // Ready state - show player
  if (status === "ready" && videoId) {
    // Determine aspect ratio from video dimensions
    const width = videoData?.width || 1920;
    const height = videoData?.height || 1080;
    const isVertical = height > width;
    const aspectPadding = (height / width * 100).toFixed(2);
    
    return (
      <div className={isVertical ? 'flex justify-center' : ''}>
        <div className={`rounded-lg overflow-hidden bg-black ${isVertical ? 'w-full max-w-[320px] sm:max-w-[300px]' : 'w-full'} ${className}`}>
          <div className="relative w-full" style={isVertical ? { aspectRatio: `${width} / ${height}`, maxHeight: '480px' } : { paddingBottom: `${aspectPadding}%` }}>
            <iframe
              src={`https://iframe.cloudflarestream.com/${videoId}?poster=${encodeURIComponent(videoData?.thumbnail || '')}`}
              className={`border-0 ${isVertical ? 'w-full h-full' : 'absolute top-0 left-0 w-full h-full'}`}
              allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              title="Video player"
            />
          </div>
        </div>
      </div>
    );
  }

  return null;
}
